import type { TileLayout } from './protocol.js';

export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

export function rotatePoint(point: Point, degrees: number): Point {
  const radians = degreesToRadians(degrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { x: point.x * cos - point.y * sin, y: point.x * sin + point.y * cos };
}

export function localToWorld(point: Point, layout: TileLayout): Point {
  const rotated = rotatePoint(point, layout.rotation);
  return { x: layout.x + rotated.x, y: layout.y + rotated.y };
}

export function worldToLocal(point: Point, layout: TileLayout): Point {
  return rotatePoint({ x: point.x - layout.x, y: point.y - layout.y }, -layout.rotation);
}

export function tileCorners(layout: TileLayout): Point[] {
  return [
    localToWorld({ x: 0, y: 0 }, layout),
    localToWorld({ x: layout.width, y: 0 }, layout),
    localToWorld({ x: layout.width, y: layout.height }, layout),
    localToWorld({ x: 0, y: layout.height }, layout)
  ];
}

export function boundsForTiles(layouts: TileLayout[]): Bounds {
  if (layouts.length === 0) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  const corners = layouts.flatMap(tileCorners);
  return {
    minX: Math.min(...corners.map((point) => point.x)),
    minY: Math.min(...corners.map((point) => point.y)),
    maxX: Math.max(...corners.map((point) => point.x)),
    maxY: Math.max(...corners.map((point) => point.y))
  };
}

export function boundsSize(bounds: Bounds): { width: number; height: number } {
  return {
    width: Math.max(1, bounds.maxX - bounds.minX),
    height: Math.max(1, bounds.maxY - bounds.minY)
  };
}

export function fitImageInBounds(
  image: { width: number; height: number },
  bounds: Bounds
): { x: number; y: number; width: number; height: number } {
  const size = boundsSize(bounds);
  const scale = Math.min(size.width / image.width, size.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: bounds.minX + (size.width - width) / 2,
    y: bounds.minY + (size.height - height) / 2,
    width,
    height
  };
}
